import { platform } from '@/services/native-api/native-api';
import type { HubSpotOutboundCallEvent } from '@/types/hubspot';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { type HubSpotCallData, hubspotService } from '../../services/hubspot';

interface HubSpotContextType {
  isReady: boolean;
  currentCall: HubSpotCallData | null;
  currentOutboundEventRef: React.RefObject<HubSpotOutboundCallEvent | null>;
  setCurrentOutboundEvent: (event: HubSpotOutboundCallEvent | null) => void;
  resizeWidget: (width: number, height: number) => Promise<void>;
  setIsHubSpotEnvironment: (isHubSpotEnvironment: boolean) => void;
}

const HubSpotContext = createContext<HubSpotContextType | null>(null);

interface HubSpotProviderProps {
  children: React.ReactNode;
}

export const HubSpotProvider: React.FC<HubSpotProviderProps> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [currentCall] = useState<HubSpotCallData | null>(null);
  const currentOutboundEventRef = useRef<HubSpotOutboundCallEvent | null>(null);

  const setCurrentOutboundEvent = useCallback((event: HubSpotOutboundCallEvent | null) => {
    currentOutboundEventRef.current = event;
  }, []);

  useEffect(() => {
    const initializeHubSpot = async () => {
      try {
        if (platform === 'web') {
          const isInitialized = await hubspotService.initialize();
          setIsReady(isInitialized);
        }
      } catch (error) {
        console.error('[HubSpot Provider] Failed to initialize:', error);
        setIsReady(false);
      }
    };

    initializeHubSpot();
  }, []);

  const resizeWidget = useCallback(
    async (width: number, height: number): Promise<void> => {
      if (!isReady) {
        return;
      }

      try {
        await hubspotService.resizeWidget(width, height);
      } catch (error) {
        console.error('[HubSpot Provider] Failed to resize widget:', error);
      }
    },
    [isReady],
  );

  const setIsHubSpotEnvironment = useCallback((isHubSpotEnvironment: boolean) => {
    setIsReady(isHubSpotEnvironment);
  }, []);

  const contextValue: HubSpotContextType = {
    isReady,
    currentCall,
    currentOutboundEventRef,
    setCurrentOutboundEvent,
    resizeWidget,
    setIsHubSpotEnvironment,
  };

  return <HubSpotContext.Provider value={contextValue}>{children}</HubSpotContext.Provider>;
};

export const useHubSpot = (): HubSpotContextType => {
  const context = useContext(HubSpotContext);
  if (!context) {
    throw new Error('useHubSpot must be used within a HubSpotProvider');
  }
  return context;
};
